
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export function useAiArtGeneration() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generateImage = async (prompt: string, apiKey: string, referenceImageUrl?: string) => {
    if (!prompt.trim()) {
      toast.error("Por favor, forneça uma descrição para gerar a imagem");
      return null;
    }

    if (!apiKey.trim()) {
      toast.error("É necessário fornecer uma chave de API da OpenAI");
      return null;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Log the request details
      console.log("Generating image with:", { 
        promptLength: prompt.length,
        hasApiKey: !!apiKey, 
        hasReferenceImage: !!referenceImageUrl 
      });

      // Fazer a chamada para a função Edge
      const { data, error: functionError } = await supabase.functions.invoke('generate-ai-art', {
        body: { prompt, apiKey, referenceImageUrl }
      });

      console.log("Edge function response:", { data, functionError });

      if (functionError) {
        console.error("Error calling function:", functionError);
        throw new Error(functionError.message || "Erro ao chamar a função de geração de imagem");
      }

      if (!data) {
        throw new Error("Nenhum dado retornado pela função");
      }

      if (data.error) {
        console.error("Function returned error:", data.error);
        
        // Tratamento específico de erros da OpenAI baseado na mensagem
        let errorMessage = data.error;
        
        if (typeof errorMessage === 'string') {
          if (errorMessage.includes("exceeded your current quota") || errorMessage.includes("billing_hard_limit_reached")) {
            errorMessage = "Limite de crédito da API OpenAI atingido. Verifique sua conta e adicione créditos para continuar gerando imagens.";
          } else if (errorMessage.includes("API key") || errorMessage.includes("Incorrect API key")) {
            errorMessage = "Chave de API inválida. Verifique se a chave está correta e tente novamente.";
          } else if (errorMessage.includes("content policy")) {
            errorMessage = "O conteúdo solicitado viola as políticas da OpenAI. Por favor, tente uma descrição diferente.";
          } else if (errorMessage.includes("rate limit")) {
            errorMessage = "Limite de taxa da API OpenAI atingido. Por favor, aguarde um momento e tente novamente.";
          } else if (errorMessage.includes("non-2xx status") || errorMessage.includes("status 5")) {
            errorMessage = "Erro no servidor da OpenAI. Por favor, tente novamente mais tarde.";
          }
        }
        
        throw new Error(errorMessage);
      }

      if (!data.imageUrl) {
        throw new Error("URL da imagem não encontrada na resposta");
      }

      setGeneratedImageUrl(data.imageUrl);
      toast.success("Imagem gerada com sucesso!");
      return data.imageUrl;
    } catch (err: any) {
      console.error("Generate image error:", err);
      
      let errorMessage = "Erro ao gerar a imagem";
      
      if (err && typeof err === 'object') {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const reset = () => {
    setGeneratedImageUrl(null);
    setError(null);
  };

  return {
    isGenerating,
    generatedImageUrl,
    error,
    generateImage,
    reset
  };
}
